import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeIcon, LayersIcon, SearchXIcon, StoreIcon } from 'lucide-react';
import { DomainHeader } from '../../components/ui/DomainHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { EmptyState } from '../../components/ui/EmptyState';
import { ChoiceGroup } from '../../components/ui/ChoiceGroup';
import { Input } from '../../components/ui/Field';
import { ResourceCard } from '../../components/marketplace/ResourceCard';
import { ProviderCard } from '../../components/marketplace/ProviderCard';
import { OrderStateBadge, OrderStateTrack } from '../../components/marketplace/OrderStateTrack';
import { knowledgeResources, myEntitlements, myOrders, serviceCategories, serviceProviders } from '../../data/marketplace';
import { useDataMode } from '../../contexts/DataModeContext';
import { FirstRun } from '../../components/platform/FirstRun';
import { naira } from '../../utils/format';
const TABS = ['Knowledge', 'Services & mentorship', 'Your orders', 'Engine'] as const;
const KNOWLEDGE_FILTERS = ['All', 'Textbook', 'Past questions', 'Video course', 'Research paper'] as const;
export function Marketplace() {
  const {
    isNew
  } = useDataMode();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Knowledge');
  const [kind, setKind] = useState<(typeof KNOWLEDGE_FILTERS)[number]>('All');
  const [category, setCategory] = useState<string>('All');
  const [query, setQuery] = useState('');
  const [openOrderId, setOpenOrderId] = useState<string>(myOrders[0]?.id ?? '');
  if (isNew) {
    return <FirstRun surface="Marketplace" what="Course materials, and people who will help you with specific work." why="Buy or rent a text and keep the licence for exactly as long as it lasts; hire a tutor, editor or mentor with the money held until you confirm the work arrived. One set of rules across both." note="You have no orders yet, so nothing here is at risk. When you do order, the money sits in escrow rather than going straight to the seller — and every fee is itemised before you commit." steps={[{
      label: 'Look for a text you need',
      detail: 'Search by course. Each result states whether you would own it or rent it, and exactly what the licence permits.'
    }, {
      label: 'Or find someone to help',
      detail: 'Providers show what they have actually completed and what their academic standing has been checked to.'
    }, {
      label: 'Understand what protects you',
      detail: 'Escrow, the review window, disputes and refunds all apply from your first order. Nothing is retroactive.',
      to: '/support'
    }]} data-id="element-10867" data-mp="9kmqhnq6envk" />;
  }
  const visibleResources = knowledgeResources.filter(resource => {
    if (kind !== 'All' && resource.kind !== kind) return false;
    if (query.trim().length > 1 && !`${resource.title} ${resource.author} ${resource.field}`.toLowerCase().includes(query.trim().toLowerCase())) {
      return false;
    }
    return true;
  });
  const visibleProviders = serviceProviders.filter(provider => {
    if (category !== 'All' && provider.category !== category) return false;
    if (query.trim().length > 1 && !`${provider.displayName} ${provider.headline} ${provider.skills.join(' ')}`.toLowerCase().includes(query.trim().toLowerCase())) {
      return false;
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-10868" data-mp="140xbjcmi67d5">
      <DomainHeader domain="marketplace" eyebrow="One marketplace engine" title="Marketplace" description="Knowledge, services and mentorship run on the same listings, orders, escrow, reviews and dispute infrastructure as housing and opportunities." actions={<Badge tone="brand" data-id="element-10870" data-mp="u065qvzbcctn">Escrow protected</Badge>} data-id="element-10869" data-mp="gd61kfofq6f1" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Marketplace sections" data-id="element-10871" data-mp="u0fu6f4pmbx8" />

      {(tab === 'Knowledge' || tab === 'Services & mentorship') && <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-10872" data-mp="13qhfx09lbi8z">
          <div className="sm:max-w-xs" data-id="element-10873" data-mp="17rkwxq3djkbs">
            <Input value={query} onChange={event => setQuery(event.target.value)} placeholder={tab === 'Knowledge' ? 'Search resources' : 'Search providers and skills'} aria-label="Search the marketplace" data-id="element-10874" data-mp="17qjciv0gxlhe" />
          </div>
          {tab === 'Knowledge' ? <ChoiceGroup options={KNOWLEDGE_FILTERS} value={kind} onChange={setKind} size="sm" data-id="element-10875" data-mp="3jh3t82tivfyg" /> : <ChoiceGroup options={['All', ...serviceCategories.map(item => item.category)]} value={category} onChange={setCategory} size="sm" data-id="element-10876" data-mp="3ofoqpuhe3lj7" />}
        </div>}

      {tab === 'Knowledge' && <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-10877" data-mp="1wss3838jv2t2">
          <div className="space-y-3" data-id="element-10878" data-mp="1wt0g3v4oa849">
            {visibleResources.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-10879" data-mp="1rv8qj7ri2y38">
                <EmptyState icon={<SearchXIcon className="h-4 w-4" data-id="element-10881" data-mp="2oj56qbj0zrdx" />} title="No resources match" description="Try a different keyword or clear the type filter." data-id="element-10880" data-mp="2oiwumluq8q3s" />
              </div> : visibleResources.map(resource => <ResourceCard key={resource.id} resource={resource} entitlement={myEntitlements.find(entitlement => entitlement.resourceId === resource.id)} data-id="element-10882" data-mp="2ajnipydk0i5m" />)}
          </div>
          <div className="space-y-6" data-id="element-10883" data-mp="10k2sbwgplqvq">
            <Panel title="Your entitlements" bodyClassName="p-0" data-id="element-10884" data-mp="15ls5piremgmz">
              <ul className="divide-y divide-line" data-id="element-10885" data-mp="15iw9pzz4a216">
                {myEntitlements.map(entitlement => {
              const resource = knowledgeResources.find(item => item.id === entitlement.resourceId);
              return <li key={entitlement.resourceId} className="px-5 py-3" data-id="element-10886" data-mp="hucxj9cy553t">
                      <p className="text-[13px] font-medium text-ink" data-id="element-10887" data-mp="mn64m3vw0pj2">{resource?.title}</p>
                      <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-10888" data-mp="8w146zkp3t7o">
                        {entitlement.model === 'free' ? 'Free access' : entitlement.model === 'rental' ? 'Rental' : 'Purchase'}{' '}
                        · granted {entitlement.grantedOn}
                      </p>
                      <Badge tone={entitlement.state === 'active' ? 'ok' : 'warn'} className="mt-1.5" data-id="element-10889" data-mp="3ggabvp6609gj">
                        {entitlement.state === 'active' ? entitlement.expiresOn ? `Active until ${entitlement.expiresOn}` : 'Active' : `Expired ${entitlement.expiresOn}`}
                      </Badge>
                    </li>;
            })}
              </ul>
              <div className="border-t border-line px-5 py-3" data-id="element-10890" data-mp="3ue8qmvoee7wk">
                <p className="text-[12px] leading-5 text-ink-muted" data-id="element-10891" data-mp="cyse6ak3witu">
                  Entitlement, not payment, decides access. An expired rental keeps its transaction
                  record but loses its access period.
                </p>
              </div>
            </Panel>
            <Panel title="Rights and licensing" data-id="element-10892" data-mp="lx456dovximt">
              <p className="text-[13px] leading-6 text-ink-muted" data-id="element-10893" data-mp="hywofiuf8hbm">
                Every resource declares preview, download and print permissions plus its territory.
                Redistribution is never granted, and protected resources are delivered through
                access-controlled links.
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Services & mentorship' && <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-10894" data-mp="22c2ig7pz2ado">
          <div className="space-y-3" data-id="element-10895" data-mp="15n4ogrq1qghk">
            {visibleProviders.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-10896" data-mp="1akwlu19gmut5">
                <EmptyState icon={<SearchXIcon className="h-4 w-4" data-id="element-10898" data-mp="1elyqwyq26131" />} title="No providers match" description="Try another category, or search by a skill such as “IELTS” or “React”." data-id="element-10897" data-mp="1en1i77ram5jh" />
              </div> : visibleProviders.map(provider => <ProviderCard key={provider.id} provider={provider} data-id="element-10899" data-mp="qw4f8khh6ikd" />)}
          </div>
          <div className="space-y-6" data-id="element-10900" data-mp="jdecvcx2znuk">
            <Panel title="Categories are configuration" bodyClassName="p-0" data-id="element-10901" data-mp="eetlm2zt4ei9">
              <ul className="divide-y divide-line" data-id="element-10902" data-mp="ecql2p1132za">
                {serviceCategories.map(item => <li key={item.id} className="px-5 py-3" data-id="element-10903" data-mp="af2ozinln45t">
                    <p className="text-[13px] font-medium text-ink" data-id="element-10904" data-mp="5ddaft1648cz">{item.category}</p>
                    <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-10905" data-mp="g3poqoq4kvh">
                      {item.subcategories.join(' · ')}
                    </p>
                  </li>)}
              </ul>
              <div className="border-t border-line px-5 py-3" data-id="element-10906" data-mp="3mvfyqpzduz3q">
                <p className="text-[12px] leading-5 text-ink-muted" data-id="element-10907" data-mp="3itl1pxohj7vr">
                  Category → subcategory → skill → service is administered from the admin console, so
                  new legitimate service types are added without a release.
                </p>
              </div>
            </Panel>
            <Panel title="Post a request instead" data-id="element-10908" data-mp="pmdhp5xx7ine">
              <p className="text-[13px] leading-6 text-ink-muted" data-id="element-10909" data-mp="pkskt9o4vnmd">
                Describe what you need and let providers submit proposals. Both directions —
                listings and requests — use the same order and escrow flow.
              </p>
              <Button size="sm" variant="secondary" className="mt-3" data-id="element-10910" data-mp="udbxm4ezyeq4">
                Post a service request
              </Button>
            </Panel>
          </div>
        </div>}

      {tab === 'Your orders' && <div className="space-y-3" data-id="element-10911" data-mp="2o0fatf93t0p">
          {myOrders.map(order => {
        const open = order.id === openOrderId;
        return <article key={order.id} className="rounded-card border border-line bg-surface" data-id="element-10912" data-mp="bndpxpf42nn0">
                <button type="button" onClick={() => setOpenOrderId(open ? '' : order.id)} aria-expanded={open} className="flex w-full flex-wrap items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-10913" data-mp="1s1dmcqeqzv5i">
                  <div className="min-w-0 flex-1" data-id="element-10914" data-mp="3a01vxf523xth">
                    <p className="text-[14px] font-semibold text-ink" data-id="element-10915" data-mp="39wmfo8znciqh">{order.title}</p>
                    <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-10916" data-mp="2w1alc2pekua3">
                      {order.reference} · {order.counterparty} (@{order.counterpartyHandle}) · placed{' '}
                      {order.placed}
                    </p>
                    <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-10917" data-mp="3sq01noldtety">{order.dueLabel}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3" data-id="element-10918" data-mp="3ex0ovuczhyq3">
                    <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-10919" data-mp="3jwwshshrro54">
                      {naira(order.amountNaira)}
                    </span>
                    <OrderStateBadge state={order.state} data-id="element-10920" data-mp="bg20j82ifo5t" />
                  </div>
                </button>
                {open && <div className="border-t border-line px-5 py-4" data-id="element-10921" data-mp="q41z4wf9ikwz">
                    <OrderStateTrack order={order} data-id="element-10922" data-mp="q3ag0l2uqfnl" />
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-3" data-id="element-10923" data-mp="l4flilviv2nn">
                      {order.state === 'BUYER_REVIEW' && <>
                          <Button size="sm" variant="primary" data-id="element-10924" data-mp="b71jvse9cife">
                            Approve and release
                          </Button>
                          <Button size="sm" variant="danger" data-id="element-10925" data-mp="75zfkx5hhg6k">
                            Raise a dispute
                          </Button>
                        </>}
                      {order.state === 'IN_PROGRESS' && <Button size="sm" variant="secondary" data-id="element-10926" data-mp="3olpewwziwwar">
                          Message provider
                        </Button>}
                      <Link to="/money" data-id="element-10927" data-mp="3jltc312kbaws">
                        <Button size="sm" variant="ghost" data-id="element-10928" data-mp="3fl9ho59dilec">
                          View in ledger
                        </Button>
                      </Link>
                    </div>
                  </div>}
              </article>;
      })}
        </div>}

      {tab === 'Engine' && <div className="grid gap-6 lg:grid-cols-3" data-id="element-10929" data-mp="244op5g9xrq9d">
          <Panel className="lg:col-span-2" title="Verticals on one engine" bodyClassName="p-0" data-id="element-10930" data-mp="241u5hqyqy9h0">
            <ul className="divide-y divide-line" data-id="element-10931" data-mp="290dszvszntbh">
              {[{
            name: 'Knowledge',
            shape: 'Resource → edition → licence → entitlement → access period',
            to: null
          }, {
            name: 'Housing',
            shape: 'Property → building → floor → room → bed → lease',
            to: '/housing'
          }, {
            name: 'Services and mentorship',
            shape: 'Provider → package or session → order → delivery',
            to: null
          }, {
            name: 'Opportunity',
            shape: 'Employer → role → application → stage',
            to: '/opportunities'
          }, {
            name: 'Events',
            shape: 'Organizer → event → ticket → attendance',
            to: '/community'
          }].map(vertical => <li key={vertical.name} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-10932" data-mp="3j084b6w0u1k7">
                  <div className="min-w-0 flex-1" data-id="element-10933" data-mp="3e269xpt5aftf">
                    <p className="text-[13px] font-semibold text-ink" data-id="element-10934" data-mp="3e1dehkzmzcql">{vertical.name}</p>
                    <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-10935" data-mp="3s0w9rd7cmtlh">{vertical.shape}</p>
                  </div>
                  {vertical.to && <Link to={vertical.to} data-id="element-10936" data-mp="1xn4zcbh745oz">
                      <Button size="sm" variant="ghost" icon={<HomeIcon className="h-3.5 w-3.5" data-id="element-10938" data-mp="1sobnenhk4mqx" />} data-id="element-10937" data-mp="1xnwwvrucsdhw">
                        Open
                      </Button>
                    </Link>}
                </li>)}
            </ul>
          </Panel>
          <div className="space-y-6" data-id="element-10939" data-mp="d5m6pfafxn6p">
            <Panel title="Shared services" data-id="element-10940" data-mp="d4kuv8llownx">
              <p className="flex items-start gap-2 text-[13px] leading-6 text-ink-muted" data-id="element-10941" data-mp="3vjvebx3su7x9">
                <LayersIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-10942" data-mp="3fb0fm3ab1wn" />
                Listings, search, verification, pricing, orders, bookings, escrow, reviews, messaging,
                disputes, refunds, payouts, fees and moderation are built once and consumed by every
                vertical.
              </p>
            </Panel>
            <Panel title="Sell on Campuzon" data-id="element-10943" data-mp="3cm29gp9m7nx8">
              <p className="text-[13px] leading-6 text-ink-muted" data-id="element-10944" data-mp="3glt6hapqj0a7">
                Providers and authors get their own consoles for services, packages, orders, escrow,
                entitlements, earnings and payouts.
              </p>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-10945" data-mp="1etesgmqbafz2">
                <Link to="/provider" data-id="element-10946" data-mp="2nrem2b7o287i">
                  <Button size="sm" variant="secondary" icon={<StoreIcon className="h-3.5 w-3.5" data-id="element-10948" data-mp="2npa9l4vpc0vm" />} data-id="element-10947" data-mp="2nsfxwhwiayqa">
                    Provider console
                  </Button>
                </Link>
                <Link to="/author" data-id="element-10949" data-mp="2erzvlbri1xn0">
                  <Button size="sm" variant="secondary" data-id="element-10950" data-mp="2er88yqfvra8v">
                    Author console
                  </Button>
                </Link>
              </div>
            </Panel>
          </div>
        </div>}
    </div>;
}